import * as d3 from 'd3';

export const DEFAULT_TIME_ZONE = 'America/Los_Angeles';

function normalizeTimeZone(timeZone) {
  const normalized = String(timeZone ?? '').trim();
  return normalized || DEFAULT_TIME_ZONE;
}

function resolveDate(value) {
  if (value == null || value === '') {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  return parseUtcDateString(value);
}

export function parseUtcDateString(value) {
  if (value == null || value === '') {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const normalized = String(value).trim();
  if (!normalized) {
    return null;
  }

  if (/[zZ]$/.test(normalized) || /[+-]\d{2}:\d{2}$/.test(normalized)) {
    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const match = normalized.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/,
  );
  if (match) {
    const [, year, month, day, hour, minute, second] = match;
    const parsed = new Date(Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second ?? 0),
    ));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function resolvePortfolioTimeZone(portfolios, portfolioKey) {
  if (!Array.isArray(portfolios) || portfolios.length === 0) {
    return DEFAULT_TIME_ZONE;
  }

  if (portfolioKey == null || portfolioKey === '') {
    return portfolios[0]?.timezone ?? DEFAULT_TIME_ZONE;
  }

  const normalizedKey = String(portfolioKey).trim().toLowerCase();
  const portfolio = portfolios.find((item) => {
    const portfolioName = String(item?.portfolio_name ?? '').trim().toLowerCase();
    const plantSlug = String(item?.plant_name ?? '').trim().toLowerCase().replace(/\s+/g, '-');
    return portfolioName === normalizedKey || plantSlug === normalizedKey;
  });

  return portfolio?.timezone ?? DEFAULT_TIME_ZONE;
}

export function createPortfolioDateFormatter(timeZone = DEFAULT_TIME_ZONE) {
  const tz = normalizeTimeZone(timeZone);

  const formatDate = (date, options = {}) => {
    const parsed = resolveDate(date);
    if (!parsed) {
      return '';
    }

    return new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      ...options,
    }).format(parsed);
  };

  const formatUtc = (value, options = {}) => {
    const parsed = resolveDate(value);
    if (!parsed) {
      return value == null || value === '' ? '' : String(value);
    }

    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZoneName: 'short',
      ...options,
    }).formatToParts(parsed);

    const getPart = (type) => parts.find((part) => part.type === type)?.value ?? '';
    return `${getPart('year')}-${getPart('month')}-${getPart('day')} ${getPart('hour')}:${getPart('minute')} ${getPart('timeZoneName')}`;
  };

  const getDateParts = (date) => {
    const parsed = resolveDate(date);
    if (!parsed) {
      return null;
    }

    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    }).formatToParts(parsed);

    const get = (type) => {
      const value = parts.find((part) => part.type === type)?.value;
      return value != null ? Number(value) : NaN;
    };

    return {
      year: get('year'),
      month: get('month'),
      day: get('day'),
      hour: get('hour'),
      minute: get('minute'),
    };
  };

  const getHour = (date) => getDateParts(date)?.hour ?? 0;
  const getAbbreviation = (date = new Date()) => {
    const parsed = resolveDate(date) ?? new Date();
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short',
    }).formatToParts(parsed);
    return parts.find((part) => part.type === 'timeZoneName')?.value ?? '';
  };

  const formatAxisDate = (date) => formatDate(date, { month: 'short', day: 'numeric' });
  const formatAxisTime = (date) => formatDate(date, { hour: '2-digit', minute: '2-digit', hour12: false });
  const formatAxisDateTime = (date) => `${formatAxisDate(date)} ${formatAxisTime(date)}`;
  const formatAxisMonthYear = (date) => formatDate(date, { month: 'short', year: 'numeric' });
  const formatAxisMonthShortYear = (date) => {
    const parts = getDateParts(date);
    if (!parts) {
      return '';
    }
    const month = formatDate(date, { month: 'short' });
    return `${month} '${String(parts.year).slice(-2)}`;
  };
  const formatDateOnly = (date) => {
    const parsed = resolveDate(date);
    if (!parsed) {
      return '';
    }

    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(parsed);

    const getPart = (type) => parts.find((part) => part.type === type)?.value ?? '';
    return `${getPart('year')}-${getPart('month')}-${getPart('day')}`;
  };
  const formatAxisMdY = (date) => formatDate(date, {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });

  const pickXAxisConfig = (days) => {
    if (days <= 2) {
      return {
        interval: d3.timeHour.every(6),
        fmt: formatAxisDateTime,
      };
    }
    if (days <= 7) {
      return { interval: d3.timeDay.every(1), fmt: formatAxisDate };
    }
    if (days <= 31) {
      const step = Math.max(1, Math.round(days / 8));
      return { interval: d3.timeDay.every(step), fmt: formatAxisDate };
    }
    if (days <= 90) {
      return { interval: d3.timeWeek.every(2), fmt: formatAxisDate };
    }
    if (days <= 366) {
      return { interval: d3.timeMonth.every(1), fmt: formatAxisMonthYear };
    }
    return { interval: d3.timeMonth.every(2), fmt: formatAxisMonthYear };
  };

  return {
    timeZone: tz,
    formatDate,
    formatUtc,
    getDateParts,
    getHour,
    getAbbreviation,
    formatAxisDate,
    formatAxisTime,
    formatAxisDateTime,
    formatAxisMonthYear,
    formatAxisMonthShortYear,
    formatAxisMdY,
    formatDateOnly,
    pickXAxisConfig,
  };
}
